<!-- eslint-disable max-len -->
<style scoped>
  .map-alert {
    position: absolute;
    bottom: 0px;
    left: 20px;
    z-index: 9999
  }
  #esriMap {
    padding: 0;
    margin: 0;
    width: 100%;
    min-height: 480px;
    height: 100%;
  }
  .management-plan-map-overlay {
    height: 100%;
  }
  .portal-item-list {
    padding: 10px;
  }
  .portal-item-label {
    padding-left: 6px;
  }
</style>
<template>
  <div id="management-plan-map">
    <div class='card'>
      <div class='card-body'>
        <span class="section-title">Step 2: </span>
        <span>Select Site(s)</span>
        <div id="esriMap">
          <b-alert class="map-alert" :show="true" dismissible variant="info">Click sites to add to management plan boundary<br/>Click again to remove</b-alert>
        </div>
        <div style="display:none;">
          <div ref="portalItemList" class="esri-widget esri-widget--panel portal-item-list"/>
          <input ref="portalItemRadioButton" type="radio" name="webmap"/>
          <label ref="portalItemLabel" class="portal-item-label"/>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable padded-blocks, no-param-reassign, prefer-destructuring */
import { mapState } from 'vuex';
import { loadModules } from 'esri-loader';

export default {
  name: 'ManagementPlanMap',
  computed: {
    ...mapState(['config']),
    ...mapState('ManagementPlan', {
      managementPlan: (state) => state.managementPlan,
    }),
    ...mapState('SiteAssessment', {
      siteAssessment: (state) => state.siteAssessment,
    }),
  },
  data() {
    return {
      dismissEditInstructions: 0,
    };
  },
  mounted() {
    this.managementPlan.sites = [];
    this.loadMap();
    this.showEditInstructions();
  },
  methods: {
    async addBasemaps(basemapsConfig) {

      await loadModules(['esri/Basemap', 'esri/layers/TileLayer', 'esri/Map', 'esri/WebMap']).then(async ([Basemap, TileLayer, Map, WebMap]) => {

        this.maps = [];

        basemapsConfig.forEach((item) => {

          if (item.class === 'TileLayerBaseMap') {

            const tileLayer = new TileLayer(item.object);

            const basemap = new Basemap({
              baseLayers: [tileLayer],
            });

            this.maps.push(new Map({
              title: item.object.title,
              basemap,
            }));

          }

          if (item.class === 'Map') {
            this.maps.push(new Map(item.object));
          }

          if (item.class === 'WebMap') {
            const webMap = new WebMap(item.object);
            webMap.allLayers.on('change', () => {
              const targetLayer = webMap.allLayers.items.find((layer) => layer.id === 'World_Transportation_5177');
              if (targetLayer) {
                targetLayer.visible = true;
              }
            });
            this.maps.push(webMap);
          }

        });
      });

    },
    async addLayers(layersConfig) {
      await loadModules(['esri/layers/FeatureLayer']).then(async ([FeatureLayer]) => {

        this.layers = layersConfig.map((referenceLayer) => new FeatureLayer(referenceLayer));

        this.huc10Layer = this.layers.filter((layer) => layer.id === 'huc10FeatureLayer')[0];
        this.huc12Layer = this.layers.filter((layer) => layer.id === 'huc12FeatureLayer')[0];
        this.siteLayer = this.layers.filter((layer) => layer.id === 'siteFeatureLayer')[0];

        this.layers.forEach((layer) => this.view.map.add(layer));

      });
    },
    async addLayerList() {

      await loadModules(['esri/widgets/Expand', 'esri/widgets/LayerList']).then(async ([Expand, LayerList]) => {

        this.layerList = new LayerList({
          container: document.createElement('div'),
          view: this.view,
        });

        this.layerListExpand = new Expand({
          expandIconClass: 'esri-icon-layer-list',
          expandTooltip: 'Expand Layers',
          view: this.view,
          group: 'legend-buttons',
          content: this.layerList,
        });

        this.view.ui.add(this.layerListExpand, { position: 'top-right' });
      });

    },
    addLegend() {

      // https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html
      // https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Expand.html
      // https://developers.arcgis.com/javascript/latest/guide/esri-icon-font/

      loadModules(['esri/widgets/Expand', 'esri/widgets/Legend']).then(([Expand, Legend]) => {

        this.legendExpand = new Expand({
          content: new Legend({
            view: this.view,
          }),
          expandIconClass: 'esri-icon-maps',
          expandTooltip: 'Expand Legend',
          group: 'legend-buttons',
          view: this.view,
        });

        this.view.ui.add(this.legendExpand, 'top-right');

      });
    },
    async addMapPicker() {
      await loadModules(['esri/widgets/Expand']).then(async ([Expand]) => {

        this.webmapList = this.$refs.portalItemList;

        this.maps.forEach((item, index) => {

          const radioButton = this.$refs.portalItemRadioButton.cloneNode();
          radioButton.setAttribute('value', index);
          radioButton.addEventListener('click', this.radioButtonOnClick);

          if (index === 0) {
            radioButton.setAttribute('checked', '');
          }

          const label = this.$refs.portalItemLabel.cloneNode();
          label.innerText = item.title;

          this.webmapList.appendChild(radioButton);
          this.webmapList.appendChild(label);

          if (index < (this.maps.length - 1)) {
            this.webmapList.appendChild(document.createElement('br'));
          }

        });

        this.webmapListExpand = new Expand({
          expandIconClass: 'esri-icon-basemap',
          expandTooltip: 'Expand Basemaps',
          view: this.view,
          group: 'legend-buttons',
          content: this.webmapList,
        });

        this.view.ui.add(this.webmapListExpand, { position: 'top-right' });

      });
    },
    async loadMap() {

      loadModules(
        ['esri/identity/IdentityManager', 'esri/views/MapView', 'esri/widgets/Home'],
        { css: true },
      ).then(async ([IdentityManager, MapView, Home]) => {

        this.identityManager = IdentityManager;

        if (localStorage['acorn.identityManager']) {
          try {
            this.identityManager.initialize(localStorage['acorn.identityManager']);
          } catch (exception) {
            console.error('this.identityManager could not be initialized with localStorage[\'acorn.identityManager\']]');
          }
        }

        this.identityManager.on('credential-create', () => {
          try {
            localStorage['acorn.identityManager'] = JSON.stringify(this.identityManager.toJSON());
          } catch (exception) {
            console.error('localStorage[\'acorn.identityManager\'] could not be set with JSON.stringify(this.identityManager.toJSON())');
          }
        });

        await this.addBasemaps(this.config.managementPlanMap.basemaps);

        this.view = new MapView({
          container: 'esriMap',
          map: this.maps[0],
          center: this.config.managementPlanMap.defaultExtent.center,
          zoom: this.config.managementPlanMap.defaultExtent.zoom,
        });

        this.view.ui.move('zoom', 'top-right');

        this.homeButton = new Home({
          view: this.view,
        });

        this.view.ui.add({
          component: this.homeButton,
          position: 'top-right',
          index: 1,
        });

        await this.addLayers(this.config.managementPlanMap.layers);
        await this.addLayerList();
        await this.addMapPicker();
        this.addLegend();

        this.viewOnClickHandle = this.view.on('click', this.viewOnClick);

      });

    },
    radioButtonOnClick(event) {
      this.view.map = this.maps[event.target.value];
      this.layers.forEach((layer) => this.view.map.add(layer));
      this.webmapListExpand.toggle();
    },
    showEditInstructions() {
      this.dismissEditInstructions = true;
    },
    viewOnClick(event) {

      this.view.hitTest(event).then((response) => {

        const hits = response.results.filter(
          (feature) => feature.graphic.layer.id === this.siteLayer.id,
        );

        this.view.whenLayerView(this.siteLayer).then((layerView) => {
          hits.forEach((hit) => {

            const filteredSites = this.managementPlan.sites.filter(
              (site) => site.graphic.attributes.objectid === hit.graphic.attributes.objectid,
            );

            if (filteredSites.length > 0) {
              filteredSites.forEach((filteredSite) => {
                filteredSite.highlight.remove();
              });
              this.managementPlan.sites = this.managementPlan.sites.filter(
                (site) => site.graphic.attributes.objectid !== hit.graphic.attributes.objectid,
              );
            } else {
              // Get the actual graphic here
              const query = this.siteLayer.createQuery();
              query.objectIds = [hit.graphic.attributes.objectid];
              this.siteLayer.queryFeatures(query).then((results) => {
                this.$store.dispatch(
                  'SiteAssessment/fetchExistingSiteAssessment',
                  results.features[0].attributes.insightlyid,
                ).then(() => {
                  results.features[0].attributes.siteAssessmentStatus = this.siteAssessment.status;
                  this.managementPlan.sites.push({
                    graphic: results.features[0],
                    highlight: layerView.highlight(hit.graphic),
                  });
                }).catch(() => {
                  results.features[0].attributes.siteAssessmentStatus = null;
                  this.managementPlan.sites.push({
                    graphic: results.features[0],
                    highlight: layerView.highlight(hit.graphic),
                  });
                });
              });
            }
          });
        });

      });

      return true;
    },
  },
};
</script>
